import {
  CalendarMonthOutlined,
  CheckroomOutlined,
  NotificationsActiveOutlined,
  SearchOffOutlined,
} from "@mui/icons-material";
import { Box } from "@mui/material";
import React, { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { CardSkeleton } from "@/components/common/CardSkeleton";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import { getReportNetworkLoading } from "@/hooks/getReportNetworkLoading";
import { useFittingRoomAreaUsage } from "@/hooks/useFittingRoomAreaUsage";
import {
  type SessionsChartMetric,
  type SessionsReportDay,
  useSessionsReport,
} from "@/hooks/useSessionsReport";
import { downloadCsv } from "@/pages/reports/shared/storeInsightsCsv";
import { StoreInsightsReportHeader } from "@/pages/reports/shared/StoreInsightsReportHeader";
import { StoreInsightsSummaryCardsRow } from "@/pages/reports/shared/StoreInsightsSummaryCardsRow";
import { TabbedReportChartCard } from "@/pages/reports/shared/TabbedReportChartCard";
import { useStoreInsightsReportFilters } from "@/pages/reports/shared/useStoreInsightsReportFilters";
import { shouldShowInsightsTrends } from "@/pages/store-insights/shared/insightsFeatureGates";
import { useStoreInsightsTrendComparison } from "@/pages/store-insights/shared/useStoreInsightsTrendComparison";
import { formatTrendChange } from "@/utils/analyticsTrend";
import { formatDecimal, formatInteger, formatPercentFromWhole } from "@/utils/number";
import { UserRole } from "@/utils/roleManager";
import {
  formatStoreDate,
  formatStoreDateKey,
  formatStoreDateRange,
  getBrowserTimezone,
} from "@/utils/storeTime";

import { FittingRoomUsagePanel } from "./FittingRoomUsagePanel";

const SessionsOverTimeChart = lazy(
  () => import("@/pages/admin/experimental/sessions/SessionsOverTimeChart"),
);

const chartMetricKeys: SessionsChartMetric[] = [
  "sessions",
  "requests",
  "unitsPerSession",
  "missedRequests",
];

const deviceTypeTranslationKeys = {
  all: "filters.deviceTypes.all",
  FITTING_ROOM: "filters.deviceTypes.fittingRoom",
  IN_AISLE: "filters.deviceTypes.inAisle",
  MOBILE_PICKUP: "filters.deviceTypes.mobile",
  RFID_CENTRAL: "filters.deviceTypes.central",
} as const;

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

const getChartMetricValue = (day: SessionsReportDay, metric: SessionsChartMetric) => {
  switch (metric) {
    case "requests":
      return day.requests;
    case "unitsPerSession":
      return day.sessions > 0 ? day.totalItems / day.sessions : 0;
    case "items":
      return day.totalItems;
    case "missedRequests":
      return day.missedRequests ?? 0;
    case "sessions":
    default:
      return day.sessions;
  }
};

const SessionsReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [metric, setMetric] = useState<SessionsChartMetric>("sessions");
  const {
    user,
    isCraveUser,
    selectedStores,
    loading,
    period,
    customRange,
    currentDateRange,
    deviceType,
    deviceTypeFilterDisabled,
    customers,
    selectedCustomerIds,
    groups,
    selectedGroups,
    groupCounts,
    filteredStores,
    onCustomerChange,
    onGroupsChange,
    onStoresChange,
    onPeriodChange,
    onCustomRangeChange,
    onDeviceTypeChange,
  } = useStoreInsightsReportFilters({
    enableDeviceFilter: true,
    defaultPeriod: "last_4_weeks",
  });
  const {
    comparisonWindow,
    setComparisonWindow,
    anchorComparisonToWeekdays,
    setAnchorComparisonToWeekdays,
  } = useStoreInsightsTrendComparison(period, currentDateRange);
  const {
    comparisonRange,
    data: reportData,
    error,
    loading: reportLoading,
  } = useSessionsReport({
    stores: selectedStores.length ? selectedStores : null,
    currentRange: currentDateRange,
    comparisonWindow,
    anchorComparisonToWeekdays:
      (period === "last_4_weeks" || period === "custom") &&
      (comparisonWindow === "month_before" || comparisonWindow === "year_before")
        ? anchorComparisonToWeekdays
        : true,
    deviceType,
  });
  const singleSelectedStore = selectedStores.length === 1 ? selectedStores[0] : null;
  const {
    areaCount: fittingRoomAreaCount,
    data: fittingRoomAreaUsage,
    error: fittingRoomAreaUsageError,
    loading: fittingRoomAreaUsageLoading,
  } = useFittingRoomAreaUsage({
    store: singleSelectedStore,
    currentRange: currentDateRange,
  });
  const currentLanguage = i18n.resolvedLanguage || i18n.language || "en-US";
  const locale = useMemo(() => new Intl.Locale(currentLanguage), [currentLanguage]);
  const browserTimezone = getBrowserTimezone();
  const networkLoading = getReportNetworkLoading(
    loading,
    reportLoading,
    fittingRoomAreaUsageLoading,
  );
  const selectionLoading = !selectedStores.length && filteredStores.length > 0;
  const isCraveAdmin = user?.role === UserRole.CRAVE_ADMIN;
  const showTrends = shouldShowInsightsTrends(isCraveUser);
  const availableChartMetricKeys = isCraveAdmin ? [...chartMetricKeys, "items"] : chartMetricKeys;
  const chartLoading = loading || selectionLoading || reportLoading;
  const chartLoadingKey = useMemo(
    () =>
      [
        selectedStores.map((store) => store.id).join(","),
        currentDateRange.start.getTime(),
        currentDateRange.end.getTime(),
        deviceType,
      ].join("|"),
    [currentDateRange.end, currentDateRange.start, deviceType, selectedStores],
  );
  const showFittingRoomUsage = Boolean(singleSelectedStore) && (fittingRoomAreaCount ?? 0) > 0;
  const showFittingRoomUsageBesideChart = showFittingRoomUsage && fittingRoomAreaCount === 1;
  const isSingleDayRange =
    formatStoreDateKey(currentDateRange.start, browserTimezone) ===
    formatStoreDateKey(currentDateRange.end, browserTimezone);

  const summary = useMemo(() => {
    const currentSessions = sum(reportData.current.map((day) => day.sessions));
    const previousSessions = sum(reportData.comparison.map((day) => day.sessions));
    const currentRequestSessions = sum(reportData.current.map((day) => day.requestSessions));
    const previousRequestSessions = sum(reportData.comparison.map((day) => day.requestSessions));
    const currentRequestRate =
      currentSessions > 0 ? (currentRequestSessions / currentSessions) * 100 : null;
    const previousRequestRate =
      previousSessions > 0 ? (previousRequestSessions / previousSessions) * 100 : null;
    const trend = (
      current: number | null,
      previous: number | null,
      lowerIsBetter: boolean,
      formatValue: (value: number) => string,
    ) => {
      if (current === null || previous === null) return { status: "unavailable" as const };
      if (previous === 0) {
        const direction = current === 0 ? ("flat" as const) : ("up" as const);
        const result = {
          status: "ready" as const,
          absoluteChange: current === 0 ? undefined : current,
          previousValue: previous,
          direction,
          isGood: direction === "flat" || !lowerIsBetter,
        };
        return {
          ...result,
          text: formatTrendChange(
            result,
            formatValue,
            (value) => formatPercentFromWhole(value, locale),
            t("sessionsReport.trends.noChange"),
          ),
        };
      }
      const difference = ((current - previous) / Math.abs(previous)) * 100;
      const direction =
        difference === 0 ? ("flat" as const) : difference > 0 ? ("up" as const) : ("down" as const);
      const result = {
        status: "ready" as const,
        percent: Math.abs(difference),
        previousValue: previous,
        direction,
        isGood: direction === "flat" || (lowerIsBetter ? direction === "down" : direction === "up"),
      };
      return {
        ...result,
        text: formatTrendChange(
          result,
          formatValue,
          (value) => formatPercentFromWhole(value, locale),
          t("sessionsReport.trends.noChange"),
        ),
      };
    };
    const currentMetricValue =
      metric === "requests"
        ? sum(reportData.current.map((day) => day.requests))
        : metric === "unitsPerSession"
          ? currentSessions > 0
            ? sum(reportData.current.map((day) => day.totalItems)) / currentSessions
            : null
          : metric === "items"
            ? sum(reportData.current.map((day) => day.totalItems))
            : metric === "missedRequests"
              ? sum(reportData.current.map((day) => day.missedRequests ?? 0))
              : currentSessions;
    const previousMetricValue =
      metric === "requests"
        ? sum(reportData.comparison.map((day) => day.requests))
        : metric === "unitsPerSession"
          ? previousSessions > 0
            ? sum(reportData.comparison.map((day) => day.totalItems)) / previousSessions
            : null
          : metric === "items"
            ? sum(reportData.comparison.map((day) => day.totalItems))
            : metric === "missedRequests"
              ? sum(reportData.comparison.map((day) => day.missedRequests ?? 0))
              : previousSessions;

    return {
      chartMetricTrend: trend(
        currentMetricValue,
        previousMetricValue,
        metric === "missedRequests",
        (value) =>
          metric === "unitsPerSession"
            ? formatDecimal(value, locale, { maximumFractionDigits: 1 })
            : formatInteger(value, locale),
      ),
      currentRequestRate,
      currentRequestSessions,
      currentSessions,
      requestTrend: trend(currentRequestRate, previousRequestRate, false, (value) =>
        formatPercentFromWhole(value, locale),
      ),
      sessionsTrend: trend(currentSessions, previousSessions, false, (value) =>
        formatInteger(value, locale),
      ),
    };
  }, [locale, metric, reportData, t]);
  const hasNoData = !chartLoading && !error && summary.currentSessions === 0;
  const currentRangeSpansYears =
    formatStoreDateKey(currentDateRange.start, browserTimezone).slice(0, 4) !==
    formatStoreDateKey(currentDateRange.end, browserTimezone).slice(0, 4);
  const comparisonRangeSpansYears =
    formatStoreDateKey(comparisonRange.start, browserTimezone).slice(0, 4) !==
    formatStoreDateKey(comparisonRange.end, browserTimezone).slice(0, 4);
  const showRangeYears = currentRangeSpansYears || comparisonRangeSpansYears;

  const chartData = useMemo(() => {
    return reportData.current.map((currentDay, index) => {
      const comparisonDay = reportData.comparison[index];
      return {
        label: formatStoreDate(currentDay.date, browserTimezone, currentLanguage, {
          month: "short",
          day: "numeric",
          year: showRangeYears ? "numeric" : undefined,
        }),
        currentDateLabel: formatStoreDate(currentDay.date, browserTimezone, currentLanguage, {
          weekday: "short",
          month: "short",
          day: "numeric",
          year: showRangeYears ? "numeric" : undefined,
        }),
        comparisonDateLabel: comparisonDay
          ? formatStoreDate(comparisonDay.date, browserTimezone, currentLanguage, {
              weekday: "short",
              month: "short",
              day: "numeric",
              year: showRangeYears ? "numeric" : undefined,
            })
          : "–",
        current: getChartMetricValue(currentDay, metric),
        comparison: comparisonDay ? getChartMetricValue(comparisonDay, metric) : 0,
      };
    });
  }, [browserTimezone, currentLanguage, metric, reportData, showRangeYears]);

  const selectedRange = formatStoreDateRange(
    currentDateRange.start,
    currentDateRange.end,
    browserTimezone,
    currentLanguage,
    {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: showRangeYears ? "numeric" : undefined,
    },
  );
  const selectedRangeLabel = `${selectedRange.startDate} – ${selectedRange.endDate}`;
  const comparisonRangeDisplay = formatStoreDateRange(
    comparisonRange.start,
    comparisonRange.end,
    browserTimezone,
    currentLanguage,
    {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: showRangeYears ? "numeric" : undefined,
    },
  );
  const comparisonRangeLabel = `${comparisonRangeDisplay.startDate} – ${comparisonRangeDisplay.endDate}`;
  const chartTitle = t("sessionsReport.sections.metricOverTime", {
    metric: t(`sessionsReport.metrics.${metric}`),
  });
  const chartMetricLabel = t(`sessionsReport.metrics.${metric}`);
  const chartTabs = availableChartMetricKeys.map((key) => ({
    value: key,
    label: t(`sessionsReport.metrics.${key}`),
    leadingContent:
      isCraveAdmin && key === "items" ? (
        <Box
          aria-hidden="true"
          sx={{
            position: "relative",
            alignSelf: "center",
            width: 28,
            height: 40,
            mx: 0.25,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              width: "1px",
              height: 6,
              transform: "translateX(-50%)",
              bgcolor: "divider",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: "50%",
              width: "1px",
              height: 6,
              transform: "translateX(-50%)",
              bgcolor: "divider",
            }}
          />
          <Box
            component="img"
            src="/images/icons/crave_icon.svg"
            alt=""
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              display: "block",
              width: 28,
              height: 28,
              objectFit: "contain",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
            }}
          />
        </Box>
      ) : undefined,
  }));
  const handleExport = useCallback(() => {
    const exportRows = [
      ...reportData.current.map((day) => [day.date, getChartMetricValue(day, metric)]),
      ...reportData.comparison.map((day) => [day.date, getChartMetricValue(day, metric)]),
    ].sort(([leftDate], [rightDate]) => String(leftDate).localeCompare(String(rightDate)));

    downloadCsv(
      [[t("sessionsReport.export.date"), chartMetricLabel], ...exportRows],
      `sessions_over_time_${metric}_${formatStoreDateKey(
        currentDateRange.start,
        browserTimezone,
      )}.csv`,
    );
  }, [browserTimezone, chartMetricLabel, currentDateRange.start, metric, reportData, t]);

  return (
    <>
      <StoreInsightsReportHeader
        title={t("sessionsReport.title")}
        icon={<CheckroomOutlined />}
        isCraveUser={isCraveUser}
        currentUserRole={user?.role}
        customers={customers}
        selectedCustomerIds={selectedCustomerIds}
        onCustomerChange={onCustomerChange}
        groups={groups}
        selectedGroups={selectedGroups}
        groupCounts={groupCounts}
        onGroupsChange={onGroupsChange}
        filteredStores={filteredStores}
        selectedStores={selectedStores}
        onStoresChange={onStoresChange}
        loading={loading}
        networkLoading={networkLoading}
        period={period}
        currentDateRange={currentDateRange}
        customRange={customRange}
        onPeriodChange={onPeriodChange}
        onCustomRangeChange={onCustomRangeChange}
        showTrendSelector
        trendPeriod={comparisonWindow}
        onTrendPeriodChange={setComparisonWindow}
        trendComparisonRange={comparisonRange}
        anchorTrendComparisonToWeekdays={anchorComparisonToWeekdays}
        onAnchorTrendComparisonToWeekdaysChange={setAnchorComparisonToWeekdays}
        deviceTypeFilterEnabled
        deviceTypeFilterDisabled={deviceTypeFilterDisabled}
        selectedDeviceType={deviceType}
        onDeviceTypeChange={onDeviceTypeChange}
      />

      <Box sx={{ p: 2.5, pt: 0, mt: 3 }}>
        <StoreInsightsSummaryCardsRow
          title={t("sessionsReport.sections.sessionSummary")}
          cards={[
            {
              title: t("sessionsReport.cards.sessions"),
              icon: <CheckroomOutlined />,
              value:
                chartLoading || summary.currentSessions === 0
                  ? "--"
                  : formatInteger(summary.currentSessions, locale),
              previousText: null,
              isPositive: true,
              subtitle: t(deviceTypeTranslationKeys[deviceType]),
              pillText: null,
              trend: showTrends
                ? chartLoading
                  ? { status: "loading" }
                  : hasNoData
                    ? undefined
                    : summary.sessionsTrend
                : undefined,
            },
            {
              title: t("sessionsReport.cards.madeRequest"),
              icon: <NotificationsActiveOutlined />,
              value:
                chartLoading || summary.currentRequestRate === null
                  ? "--"
                  : formatPercentFromWhole(summary.currentRequestRate, locale),
              previousText: null,
              isPositive: true,
              subtitle: hasNoData ? (
                <Box component="span" aria-hidden="true" sx={{ display: "block", height: 18 }} />
              ) : (
                t("sessionsReport.cards.madeRequestSubtitle", {
                  requestSessions: formatInteger(summary.currentRequestSessions, locale),
                  sessions: formatInteger(summary.currentSessions, locale),
                })
              ),
              subtitleLoading: chartLoading,
              pillText: null,
              trend: showTrends
                ? chartLoading
                  ? { status: "loading" }
                  : hasNoData
                    ? undefined
                    : summary.requestTrend
                : undefined,
            },
          ]}
        />

        <Box
          sx={
            showFittingRoomUsageBesideChart
              ? {
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    lg: "minmax(0, 2fr) minmax(320px, 1fr)",
                  },
                  gap: 2,
                  alignItems: "stretch",
                  "& > *": { minWidth: 0 },
                  "& > * > div": { height: "100%" },
                }
              : undefined
          }
        >
          <Box sx={{ height: "100%" }}>
            <TabbedReportChartCard
              title={
                isSingleDayRange ? null : (
                  <Box
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "baseline", gap: 1.25 }}
                  >
                    <Box component="span">{chartTitle}</Box>
                    {chartLoading ? (
                      <CardSkeleton
                        width={118}
                        sx={{
                          alignSelf: "baseline",
                          height: "0.75rem",
                          position: "relative",
                          top: "0.75rem",
                          transform: "none",
                        }}
                      />
                    ) : showTrends && !hasNoData && summary.chartMetricTrend.status === "ready" ? (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          alignSelf: "baseline",
                          color:
                            summary.chartMetricTrend.direction === "flat"
                              ? "text.secondary"
                              : summary.chartMetricTrend.isGood
                                ? "success.main"
                                : "warning.main",
                          fontSize: 12,
                          fontWeight: 600,
                          lineHeight: 1,
                          "&::before": {
                            content:
                              summary.chartMetricTrend.direction === "up"
                                ? '"▲"'
                                : summary.chartMetricTrend.direction === "down"
                                  ? '"▼"'
                                  : '"="',
                            fontSize: 9,
                            lineHeight: 1,
                            marginInlineEnd: "4px",
                          },
                        }}
                      >
                        {t("sessionsReport.trends.vsPreviousPeriod", {
                          value: summary.chartMetricTrend.text,
                        })}
                      </Box>
                    ) : null}
                  </Box>
                )
              }
              onExport={isSingleDayRange ? undefined : handleExport}
              exportDisabled={reportLoading || reportData.current.length === 0}
              tabs={chartTabs}
              selectedTab={metric}
              onTabChange={(value) => setMetric(value as SessionsChartMetric)}
              tabsAriaLabel={t("sessionsReport.fields.metric")}
              showTabs={!isSingleDayRange}
              legendItems={[
                { label: selectedRangeLabel },
                { label: comparisonRangeLabel, lineStyle: "dashed", color: "#98A2B3" },
              ]}
              loadingOverlay={
                !isSingleDayRange && chartLoading ? (
                  <LongRunningDataLoader
                    variant="focused"
                    isLoading={true}
                    baseLabels={[
                      t("sessionsReport.loading"),
                      t("storeInsights.common.loading.readingActivity"),
                      t("storeInsights.common.loading.comparingPeriods"),
                      t("storeInsights.common.loading.calculatingInsights"),
                      t("storeInsights.common.loading.preparingChart"),
                    ]}
                    loopingLabels={[
                      t("storeInsights.common.loading.stillWorking"),
                      t("storeInsights.common.loading.thanksForPatience"),
                    ]}
                    fixedDurationMs={30_000}
                    locale={currentLanguage}
                    loadingKey={chartLoadingKey}
                    estimateLabel={(duration) =>
                      t("storeComparison.loading.usuallyUnder", { duration })
                    }
                    containerSx={{
                      position: "absolute",
                      inset: 0,
                      minHeight: 0,
                      zIndex: 1,
                    }}
                  />
                ) : undefined
              }
            >
              {isSingleDayRange ? (
                <Box
                  sx={{
                    minHeight: 320,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "text.secondary",
                  }}
                >
                  <CalendarMonthOutlined sx={{ fontSize: 48, color: "#98A2B3", mb: 2 }} />
                  <Box sx={{ color: "text.primary", fontSize: 22, fontWeight: 700 }}>
                    {t("sessionsReport.empty.singleDayTitle")}
                  </Box>
                  <Box sx={{ mt: 1.5, maxWidth: 440, fontSize: 17, lineHeight: 1.5 }}>
                    {t("sessionsReport.empty.singleDayDescription")}
                  </Box>
                </Box>
              ) : (
                <>
                  <Box sx={{ position: "relative" }}>
                    {chartLoading ? (
                      <Box aria-hidden="true" sx={{ height: 360 }} />
                    ) : hasNoData ? (
                      <Box
                        sx={{
                          height: 360,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          color: "text.secondary",
                        }}
                      >
                        <SearchOffOutlined sx={{ fontSize: 48, color: "#98A2B3", mb: 2 }} />
                        <Box sx={{ color: "text.primary", fontSize: 22, fontWeight: 700 }}>
                          {t("sessionsReport.empty.noDataTitle")}
                        </Box>
                        <Box sx={{ mt: 1.5, maxWidth: 440, fontSize: 17, lineHeight: 1.5 }}>
                          {t("sessionsReport.empty.noDataDescription")}
                        </Box>
                      </Box>
                    ) : (
                      <Suspense fallback={<Box sx={{ height: 360 }} />}>
                        <SessionsOverTimeChart
                          data={chartData}
                          locale={locale}
                          metric={metric}
                          currentLabel={selectedRangeLabel}
                          comparisonLabel={comparisonRangeLabel}
                        />
                      </Suspense>
                    )}
                    {error ? (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "text.secondary",
                        }}
                      >
                        {t("sessionsReport.errors.failedLoad")}
                      </Box>
                    ) : null}
                  </Box>
                </>
              )}
            </TabbedReportChartCard>
          </Box>
          {showFittingRoomUsageBesideChart ? (
            <FittingRoomUsagePanel
              areas={fittingRoomAreaUsage}
              loading={fittingRoomAreaUsageLoading}
              error={fittingRoomAreaUsageError}
              locale={locale}
              selectedRangeLabel={selectedRangeLabel}
              sideBySide
              areaCount={fittingRoomAreaCount ?? 1}
              loadingKey={chartLoadingKey}
            />
          ) : null}
        </Box>
        {showFittingRoomUsage && !showFittingRoomUsageBesideChart ? (
          <FittingRoomUsagePanel
            areas={fittingRoomAreaUsage}
            loading={fittingRoomAreaUsageLoading}
            error={fittingRoomAreaUsageError}
            locale={locale}
            selectedRangeLabel={selectedRangeLabel}
            sideBySide={false}
            areaCount={fittingRoomAreaCount ?? 1}
            loadingKey={chartLoadingKey}
          />
        ) : null}
      </Box>
    </>
  );
};

export default SessionsReport;
